<script setup> 
    import { computed } from 'vue';
    import AchievementTable from '@/components/result/ai-literacy/AchievementTable.vue'; 
    import LevelRatio from '@/components/result/ai-literacy/LevelRatio.vue';

    const props = defineProps({
        teacherData: Object
    });

    const userRole = 'teacher';

    // teacherData를 기반으로 AchievementTable props 생성
    const achievementTableProps = computed(() => {
        if (!props.teacherData) {
            return {
                levelYear: 5,
                userRole: 'teacher',
                studentLevel: 4,
                schulType: 'E'
            };
        }

        const gradeCode = parseInt(props.teacherData.grade_code) || 1;
        const schulType = props.teacherData.schul_type || 'E';
        
        const levelYear = schulType === 'M' ? gradeCode : gradeCode;

        return {
            levelYear: levelYear,
            userRole: 'teacher',
            studentLevel: 4,
            schulType: schulType
        };
    });

    // teacherData의 list를 기반으로 수준별 학생 데이터 생성
    const rateTableData = computed(() => {
        const schulType = props.teacherData?.schul_type || 'E';
        
        // schulType에 따른 점수 범위 설정
        const ranges = {
            'E': { // 초등학교
                4: { min: 20.5, max: 26 },
                3: { min: 15.5, max: 20 },
                2: { min: 10, max: 15 },
                1: { min: 0, max: 9.5 }
            },
            'M': { // 중학교
                4: { min: 24, max: 28 },
                3: { min: 18, max: 23 },
                2: { min: 10, max: 17 },
                1: { min: 0, max: 9 }
            }
        };
        
        const currentRanges = ranges[schulType] || ranges['E'];
        
        if (!props.teacherData || !props.teacherData.list) {
            return [
                { level: '4', scoreRange: `${currentRanges[4].min}~${currentRanges[4].max}`, students: '' },
                { level: '3', scoreRange: `${currentRanges[3].min}~${currentRanges[3].max}`, students: '' },
                { level: '2', scoreRange: `${currentRanges[2].min}~${currentRanges[2].max}`, students: '' },
                { level: '1', scoreRange: `${currentRanges[1].min}~${currentRanges[1].max}`, students: '' }
            ];
        }

        const students = props.teacherData.list;
        
        // 성취수준별로 학생을 그룹화하고 번호순으로 정렬
        const levelGroups = {
            4: [],
            3: [],
            2: [],
            1: []
        };

        students.forEach(student => {
            const level = student.achievement_level;
            if (levelGroups[level]) {
                levelGroups[level].push(student);
            }
        });

        // 각 수준별로 학교 번호순으로 정렬
        Object.keys(levelGroups).forEach(level => {
            levelGroups[level].sort((a, b) => {
                const numA = parseInt(a.school_num) || 0;
                const numB = parseInt(b.school_num) || 0;
                return numA - numB;
            });
        });

        return [
            { 
                level: '4', 
                scoreRange: `${currentRanges[4].min}~${currentRanges[4].max}`, 
                students: levelGroups[4].map(s => s.name).join(', ')
            },
            { 
                level: '3', 
                scoreRange: `${currentRanges[3].min}~${currentRanges[3].max}`, 
                students: levelGroups[3].map(s => s.name).join(', ')
            },
            { 
                level: '2', 
                scoreRange: `${currentRanges[2].min}~${currentRanges[2].max}`, 
                students: levelGroups[2].map(s => s.name).join(', ')
            },
            { 
                level: '1', 
                scoreRange: `${currentRanges[1].min}~${currentRanges[1].max}`, 
                students: levelGroups[1].map(s => s.name).join(', ')
            }
        ];
    });

    // 성취수준 분석 메시지 생성
    const achievementMessage = computed(() => {
        if (!props.teacherData) {
            return {
                highLevelRate: '85.2',
                lowLevelRate: '14.8'
            };
        }

        const level1Count = props.teacherData.achievement1_level_cnt || 0;
        const level2Count = props.teacherData.achievement2_level_cnt || 0;
        const level3Count = props.teacherData.achievement3_level_cnt || 0;
        const level4Count = props.teacherData.achievement4_level_cnt || 0;

        const totalStudents = level1Count + level2Count + level3Count + level4Count;
        
        if (totalStudents === 0) {
            return {
                highLevelRate: '0',
                lowLevelRate: '0'
            };
        }

        // 3수준 이상 (3, 4수준) 비율
        const highLevelStudents = level3Count + level4Count;
        const highLevelRate = ((highLevelStudents / totalStudents) * 100).toFixed(1);

        // 1수준 학생 비율 (추가 지원 필요)
        const lowLevelRate = ((level1Count / totalStudents) * 100).toFixed(1);

        return {
            highLevelRate: highLevelRate,
            lowLevelRate: lowLevelRate
        };
    });
</script>
<template>
    <div class="report-inner">   
        <div class="card-wrap">
            <AchievementTable 
                :userRole="userRole" 
                :levelYear="achievementTableProps.levelYear"
                :studentLevel="achievementTableProps.studentLevel"
                :schulType="achievementTableProps.schulType"
            />
            <LevelRatio 
                :table-data="rateTableData" 
                :achievement-message="achievementMessage"
            />             
        </div>
    </div>
</template>